<template>
  <v-app>
    <v-main>
      <ImagesViewer />

      <v-container fluid class="lower-half">
        <v-row align="stretch">
          <!-- LEFT: The Form -->
          <v-col cols="12" md="6" class="pr-md-6">
            <div class="form-card elevation-2">
              <h2 class="luxury-title text-h5 font-weight-bold mb-4">
                {{ $t('bookingForm.reserveYourStay') }}
              </h2>

              <v-alert type="error" v-if="errorMsg" class="mb-4">
                {{ errorMsg }}
              </v-alert>

              <v-form ref="form" v-model="valid">
                <!-- Check-in -->
                <v-text-field
                  v-model="startDateModel"
                  :label="$t('bookingForm.checkin')"
                  readonly
                  prepend-inner-icon="mdi-calendar"
                  class="mb-4"
                  @click="openStartDialog"
                  :rules="[dateRule]"
                />

                <!-- Check-out -->
                <v-text-field
                  v-model="endDateModel"
                  :label="$t('bookingForm.checkout')"
                  readonly
                  prepend-inner-icon="mdi-calendar"
                  class="mb-4"
                  @click="openEndDialog"
                  :rules="[dateRule]"
                />

                <!-- Full Name -->
                <v-text-field
                  v-model="name"
                  :label="$t('bookingForm.fullName')"
                  :rules="[nameRule]"
                  prepend-inner-icon="mdi-account"
                  class="mb-4"
                />

                <!-- Phone -->
                <v-text-field
                  v-model="phone"
                  :label="$t('bookingForm.phone')"
                  type="tel"
                  :rules="[phoneRule]"
                  prepend-inner-icon="mdi-phone"
                  class="mb-4"
                />

                <!-- Promo Code -->
                <v-text-field
                  v-model="promoCode"
                  :label="$t('bookingForm.promoCode')"
                  prepend-inner-icon="mdi-ticket-percent"
                  class="mb-4"
                  @blur="applyPromoCode"
                  :rules="[promoCodeRule]"
                />

                <!-- Instead of a <v-select>, we use checkboxes for each add-on -->
                <div class="mb-4">
                  <h3 class="text-h6">{{ $t('bookingForm.addOns') }}</h3>
                  <div
                    v-for="(addOn, index) in addOns"
                    :key="index"
                    class="d-flex align-center mb-2"
                  >
                    <v-checkbox
                      :label="displayAddOn(addOn)"
                      :value="addOn.id"
                      v-model="selectedAddOnIDs"
                      hide-details
                    />
                  </div>
                </div>
                <a :href="pdfUrl" download="Add-ons.pdf" class="text-white"> {{ $t('bookingForm.addOnsDetails') }}</a>
                <!-- Show chosen Add-Ons if any -->
                <div v-if="selectedAddOnsDetails.length" class="mb-4">
                  <h3 class="text-h6">{{ $t('bookingForm.selectedAddOns') }}</h3>
                  <ul>
                    <li
                      v-for="(addOn, index) in selectedAddOnsDetails"
                      :key="index"
                    >
                      {{ addOn.name }} <br> {{ formatPrice(addOn.price) }} OMR
                    </li>
                  </ul>
                </div>

                <!-- Days -->
                <v-card
                  v-if="numofdays !== null"
                  class="mb-4 mt-4"
                  color="transparent"
                >
                  <div class="text-h6 ml-4">
                    {{ $t('bookingForm.numofdays') }}
                  </div>
                  <div class="text-h6 ml-6">
                    {{ numofdays }}
                  </div>
                </v-card>

                <!-- Discount -->
                <v-card
                  v-if="discount > 0"
                  class="mb-4"
                  color="transparent"
                >
                  <div class="text-h6 ml-4 text-blue">
                    {{ $t('bookingForm.discount') }}
                  </div>
                  <div class="text-h6 ml-6 text-blue">
                    -{{ formatPrice(discount) }} OMR
                  </div>
                </v-card>

                <!-- Total -->
                <v-card
                  v-if="totalPrice !== null"
                  class="mb-4"
                  color="transparent"
                >
                  <div class="text-h6 ml-4 text-grey-lighten-3 font-weight-bold">
                    {{ $t('bookingForm.totalPrice') }}
                  </div>
                  <div class="text-h6 ml-6 text-grey-lighten-3 font-weight-bold">
                    {{ formattedPrice }} OMR
                  </div>
                </v-card>

                <v-btn
                  block
                  size="x-large"
                  rounded="xl"
                  color="primary"
                  class="mt-4 luxury-button"
                  @click="submitBooking"
                  :disabled="!valid || !totalPrice"
                >
                  {{ $t('bookingForm.confirmBooking') }}
                </v-btn>
              </v-form>
            </div>
          </v-col>

          <!-- RIGHT: Additional text/info -->
          <v-col cols="12" md="6" class="pl-md-6 d-flex flex-column">
            <div class="right-side pa-8 text-center text-md-left">
              <h2 class="luxury-title text-center text-h4 font-weight-bold mb-6">
                {{ $t('bookingForm.experienceLuxury') }}
              </h2>
              <p class="text-center mb-6 font-weight-bold">
                {{ $t('bookingForm.description1') }}
              </p>
              <p class="text-center mb-10 font-weight-bold">
                {{ $t('bookingForm.description2') }}
              </p>
              <h2 class="luxury-title text-center text-h4 font-weight-bold mb-6">
                {{ $t('bookingForm.entertainment') }}
              </h2>
              <v-row justify="center" class="mt-4">
                <v-col cols="auto" class="mr-9">
                  <v-img
                    src="images/swimming.png"
                    height="120px"
                    width="120px"
                  />
                  <p class="text-center mb-6 font-weight-medium">
                    {{ $t('bookingForm.swimming') }}
                  </p>
                </v-col>
                <v-col cols="auto" class="mr-9">
                  <v-img
                    src="images/cinema.png"
                    height="120px"
                    width="120px"
                  />
                  <p class="text-center mb-6 font-weight-medium">
                    {{ $t('bookingForm.cinema') }}
                  </p>
                </v-col>
                <v-col cols="auto" class="mr-9">
                  <v-img
                    src="images/grill.png"
                    height="120px"
                    width="120px"
                  />
                  <p class="text-center mb-6 font-weight-medium">
                    {{ $t('bookingForm.grill') }}
                  </p>
                </v-col>
              </v-row>
              <v-row justify="center" class="mt-4">
                <v-col cols="auto" class="mr-9">
                  <v-img
                    src="images/wifi.png"
                    height="120px"
                    width="120px"
                  />
                  <p class="text-center mb-6 font-weight-medium">
                    {{ $t('bookingForm.wifi') }}
                  </p>
                </v-col>
                <v-col cols="auto" class="mr-9">
                  <v-img
                    src="images/snacks.png"
                    height="120px"
                    width="120px"
                  />
                  <p class="text-center mb-6 font-weight-medium">
                    {{ $t('bookingForm.snacks') }}
                  </p>
                </v-col>
                <v-col cols="auto" class="mr-9">
                  <v-img
                    src="images/coffee.png"
                    height="120px"
                    width="120px"
                  />
                  <p class="text-center mb-6 font-weight-medium">
                    {{ $t('bookingForm.coffeemaker') }}
                  </p>
                </v-col>
              </v-row>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </v-main>

    <!-- Check-in Dialog -->
    <v-dialog v-if="unavailableDatesLoaded" v-model="startDialog" max-width="330px">
      <v-card>
        <div class="text-h6">
          {{ $t('bookingForm.selectCheckin') }}
        </div>
        <v-date-picker
          v-model="startDate"
          :min="today"
          :allowed-dates="allowedCheckinDates"
          @input="closeStartDialog"
        />
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="startDialog = false">
            {{ $t('bookingForm.ok') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Check-out Dialog -->
    <v-dialog v-if="unavailableDatesLoaded" v-model="endDialog" max-width="330px">
      <v-card>
        <div class="text-h6">
          {{ $t('bookingForm.selectCheckout') }}
        </div>
        <v-date-picker
          v-model="endDate"
          :min="startDate || today"
          :allowed-dates="allowedCheckoutDates"
          @input="closeEndDialog"
        />
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="endDialog = false">
            {{ $t('bookingForm.ok') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script>
import apiService from '@/services/apiService.js'
import ImagesViewer from '@/components/ImagesViewer.vue'

export default {
  components: { ImagesViewer },
  data() {
    return {
      valid: false,
      startDate: null,
      endDate: null,
      startDateModel: '',
      endDateModel: '',
      promoCode: '',
      discount: 0,
      name: '',
      phone: '',
      errorMsg: null,
      numofdays: null,
      startDialog: false,
      endDialog: false,
      today: new Date(),
      unavailableCheckinDates: [],
      unavailableCheckoutDates: [],
      pdfUrl:'images/Add-ons.pdf',
      finalPrice:0,

      // Add-ons from API
      addOns: [],
      // We'll store the chosen add-on IDs in an array
      selectedAddOnIDs: [],
      isLoadingAddOns: false
    }
  },
  computed: {
    unavailableDatesLoaded() {
      return (
        Array.isArray(this.unavailableCheckinDates) &&
        Array.isArray(this.unavailableCheckoutDates)
      )
    },
    dateRule() {
      return v => !!v || 'Date is required'
    },
    nameRule() {
      return v => !!v || 'Name is required'
    },
    phoneRule() {
      return v => /^\d{8}$/.test(v) || 'Phone must be exactly 8 digits'
    },
    promoCodeRule() {
      return v => v === '' || /^[A-Z0-9]{3,10}$/.test(v) || 'Invalid promo code format'
    },
    selectedAddOnsDetails() {
      // Filter the addOns array by the IDs in selectedAddOnIDs
      return this.addOns.filter(a => this.selectedAddOnIDs.includes(a.id))
    },
    // basePrice() {
    //   return this.numofdays ? this.numofdays * 75 : 0
    // },
    totalPrice() {
      const addOnsPrice = this.selectedAddOnsDetails.reduce(
        (sum, a) => sum + a.price,
        0
      )
      console.log(addOnsPrice)
      console.log(this.discount)
      console.log(this.totalPrice)
      return this.finalPrice + addOnsPrice - this.discount
    },
    formattedPrice() {
      if (this.totalPrice !== null) {
        return this.formatPrice(this.totalPrice)
      }
      return ''
    }
  },
  watch: {
    startDate(newVal) {
      if (!newVal || isNaN(newVal.getTime())) {
        this.startDateModel = ''
        this.resetPricing()
      } else {
        this.startDateModel = this.formatDate(newVal)
        this.calculatePrice()
      }
    },
    endDate(newVal) {
      if (!newVal || isNaN(newVal.getTime())) {
        this.endDateModel = ''
        this.resetPricing()
      } else {
        this.endDateModel = this.formatDate(newVal)
        this.calculatePrice()
      }
    },
    startDateModel(newVal) {
      if (!newVal) {
        this.startDate = null
      } else {
        const d = new Date(newVal)
        this.startDate = isNaN(d.getTime()) ? null : d
      }
    },
    endDateModel(newVal) {
      if (!newVal) {
        this.endDate = null
      } else {
        const d = new Date(newVal)
        this.endDate = isNaN(d.getTime()) ? null : d
      }
    }
  },
  mounted() {
    // Possibly load from route
    if (this.$route.query.start_date) {
      const sd = new Date(this.$route.query.start_date)
      if (!isNaN(sd.getTime())) {
        this.startDate = sd
      } else {
        this.startDateModel = this.$route.query.start_date
      }
    }
    if (this.$route.query.end_date) {
      const ed = new Date(this.$route.query.end_date)
      if (!isNaN(ed.getTime())) {
        this.endDate = ed
      } else {
        this.endDateModel = this.$route.query.end_date
      }
    }
    if (this.startDate && this.endDate) {
      this.calculatePrice()
    }

    // fetch blocked
    apiService.fetchUnavailableDates()
      .then(r => {
        this.unavailableCheckinDates = r.data.unavailable_checkin_dates
        this.unavailableCheckoutDates = r.data.unavailable_checkout_dates
      })
      .catch(e => console.error('Failed to load unavailable dates:', e))

    // fetch add-ons
    this.fetchAddOns()
  },
  methods: {
    formatDate(dateObj) {
      const yyyy = dateObj.getFullYear()
      const mm = String(dateObj.getMonth() + 1).padStart(2, '0')
      const dd = String(dateObj.getDate()).padStart(2, '0')
      return `${yyyy}-${mm}-${dd}`
    },
    allowedCheckinDates(date) {
      const formatted = this.formatDate(date)
      return !this.unavailableCheckinDates.includes(formatted)
    },
    allowedCheckoutDates(date) {
      const formatted = this.formatDate(date)
      return !this.unavailableCheckoutDates.includes(formatted)
    },
    openStartDialog() {
      if (this.unavailableDatesLoaded) this.startDialog = true
    },
    openEndDialog() {
      if (this.unavailableDatesLoaded) this.endDialog = true
    },
    closeStartDialog() {
      this.startDialog = false
      if (this.endDate && this.startDate && this.startDate > this.endDate) {
        this.endDate = null
      }
    },
    closeEndDialog() {
      this.endDialog = false
    },
    fetchAddOns() {
      this.isLoadingAddOns = true
      apiService.fetchPublicAddOns()
        .then(r => {
          this.addOns = (r.data || []).map(addon => {
            const p = parseFloat(addon.price) || 0
            return {
              ...addon,
              price: p
            }
          })
        })
        .catch(err => {
          console.error('Failed to fetch add-ons:', err)
          this.errorMsg = 'Failed to load add-ons.'
        })
        .finally(() => {
          this.isLoadingAddOns = false
        })
    },
    calculatePrice() {
      if (!this.startDate || !this.endDate) {
        this.resetPricing()
        return
      }
      const startStr = this.formatDate(this.startDate)
      const endStr = this.formatDate(this.endDate)
      if (!startStr || !endStr) {
        this.resetPricing()
        return
      }

      let params = {
        start_date: startStr,
        end_date: endStr
      }
      if (this.promoCode) {
        params.promo_code = this.promoCode
      }

      apiService.calculatePrice(params)
        .then(r => {
          this.numofdays = r.data.numofdays
          this.discount = r.data.discount || 0
          this.finalPrice = r.data.total_price || 0
        })
        .catch(e => {
          console.error('Failed to calculate price:', e)
          this.resetPricing()
          if (e.response && e.response.data && e.response.data.error) {
            this.errorMsg = e.response.data.error
          }
        })
    },
    applyPromoCode() {
      if (!this.promoCode) {
        this.discount = 0
        this.calculatePrice()
        return
      }

      apiService.validatePromoCode({ promo_code: this.promoCode })
        .then(r => {
          if (r.data.valid) {
            this.discount = r.data.discount
            this.calculatePrice()
          } else {
            this.discount = 0
            this.errorMsg = r.data.error || 'Invalid promo code.'
          }
        })
        .catch(e => {
          this.discount = 0
          this.errorMsg = e.response?.data?.error || 'Failed to validate promo code.'
        })
    },
    submitBooking() {
      if (!this.valid) return
      this.errorMsg = null

      if (!this.startDate || !this.endDate) {
        this.errorMsg = 'Invalid date(s)'
        return
      }
      const startStr = this.formatDate(this.startDate)
      const endStr = this.formatDate(this.endDate)
      if (!startStr || !endStr) {
        this.errorMsg = 'Invalid date(s)'
        return
      }

      const bookingData = {
        start_date: startStr,
        end_date: endStr,
        name: this.name,
        phone: this.phone,
        promo_code: this.promoCode || null,
        add_ons: this.selectedAddOnIDs
      }

      apiService.submitBooking(bookingData)
        .then(r => {
          this.numofdays = r.data.numofdays
          this.discount = r.data.discount || 0
          this.calculatePrice()

          const message = r.data.message || 'Booking confirmed successfully.'
          this.$router.push({
            path: '/booking-confirmation',
            query: {
              name: this.name,
              phone: this.phone,
              total_price: this.formatPrice(this.totalPrice),
              numofdays: this.numofdays,
              discount: this.formatPrice(this.discount),
              add_ons: JSON.stringify(this.selectedAddOnsDetails),
              message
            }
          })
        })
        .catch(e => {
          if (e.response && e.response.data && e.response.data.error) {
            this.errorMsg = e.response.data.error
          } else {
            this.errorMsg = 'An unexpected error occurred.'
          }
        })
    },
    formatPrice(amount) {
      return Number(amount).toFixed(2)
    },
    resetPricing() {
      this.numofdays = null
      this.discount = 0
    },
    // For the checkbox label
    displayAddOn(addOn) {
      return `${addOn.name}-${this.formatPrice(addOn.price)}`
    }
  }
}
</script>

<style scoped>
.lower-half {
  background: linear-gradient(to right, #546E7A, #78909C);
  min-height: 30vh;
  color: #e3e3e3;
}
.form-card {
  background-color: #9E9E9E;
  border-radius: 16px;
  padding: 32px;
}
.luxury-title {
  color: #e3e3e3;
  margin-bottom: 5px;
}
.luxury-button {
  background-color: #424242 !important;
  color: #eae8f2 !important;
}
.text-green {
  color: green;
}
</style>
